import { Injectable } from '@angular/core';
import { APIService } from './api.service';

/**
 * Mirror of App\Enums\OrganizationLoginStatus. Single source of truth on
 * the frontend — used by the row interface, the status-filter coldef, and
 * any cell that switches on status.
 */
export const ORG_STATUS = ['active', 'dormant', 'inactive'] as const;
export type OrgStatus = typeof ORG_STATUS[number];

/** Intake fields on a platform row (contract 7.3). Counts and settings only, never content or the full address. */
export interface OrganizationIntakeFields {
  intake_enabled?: boolean;
  /** null when the tenant never had an address ("made on first switch-on"). */
  intake_address_masked?: string | null;
  intake_month_mails?: number | null;
  intake_month_direct?: number | null;
  intake_monthly_cap?: number | null;
  intake_held?: number | null;
  /** Quotes switch (#1697). */
  quotes_enabled?: boolean;
}

export type IntakeActionName = 'switch' | 'cap' | 'rotate' | 'revoke';

export interface OrganizationMetricsRow extends OrganizationIntakeFields {
  client_id: number;
  client_name: string;
  client_domain: string;
  onboarded_at?: string;
  logo_url?: string;
  status: OrgStatus;
  active_profile_count: number;
  logged_in_users: number;
  storage_bytes: number;
  matching_events: number;
  last_login_at?: string;
}

export interface KpiDelta {
  value: number;
  prev: number;
  delta: number;
}

export interface PlatformSummary {
  active_profile_count: KpiDelta;
  storage_bytes: KpiDelta;
  matching_events: KpiDelta;
  organizations: KpiDelta;
}

export interface PlatformOverviewResponse {
  month: string;
  summary: PlatformSummary;
  organizations: OrganizationMetricsRow[];
  /** ISO time of the last successful intake:prune run, platform wide. */
  intake_prune_last_ok?: string | null;
}

export interface TimelineSeries {
  client_id: number;
  points: number[];
}

export interface PlatformTimelineResponse {
  metric: 'active_profiles';
  months: string[];
  series: TimelineSeries[];
  platform_total: number[];
}

@Injectable({
  providedIn: 'root'
})
export class PlatformOverviewService extends APIService {

  /**
   * Fetch summary + per-organization metrics for a month (YYYY-MM).
   * Backend defaults to the previous calendar month if month is omitted.
   */
  public getOverview(month?: string): Promise<PlatformOverviewResponse> {
    const qs = month ? `?month=${encodeURIComponent(month)}` : '';
    return this.get<PlatformOverviewResponse>(
      `/platform/organizations${qs}`,
      'Could not load platform overview'
    );
  }

  /**
   * Fetch the platform-wide trend for one metric over a 6/12/24-month window.
   * Backend defaults `end` to current calendar month and `metric` to 'active_profiles'.
   */
  public getTimeline(metric: 'active_profiles', months: 6 | 12 | 24, end?: string): Promise<PlatformTimelineResponse> {
    const params = new URLSearchParams({ metric, months: String(months) });
    if (end) params.set('end', end);
    return this.get<PlatformTimelineResponse>(
      `/platform/organizations/timeline?${params.toString()}`,
      'Could not load trend'
    );
  }

  /**
   * Download the CSV export as an authenticated blob and save via an
   * ephemeral anchor. Avoids window.open, which would surface a Laravel
   * error page as a .csv on a 500 (and leak debug info in APP_DEBUG envs).
   * Mirrors TimeTrackingService.exportTimesheet — same blob + Content-
   * Disposition pattern so the filename stays a single source of truth
   * on the backend.
   */
  public async downloadCsv(month: string): Promise<void> {
    const url = `/platform/organizations.csv?month=${encodeURIComponent(month)}`;

    const response = await this.get(url, null, {
      responseType: 'blob',
      observe: 'response',
      withCredentials: true,
    });

    const contentDisposition = response.headers.get('Content-Disposition');
    const filename = contentDisposition?.match(/filename=([^;]+)/)?.[1] || `platform-overview-${month}.csv`;

    const a = document.createElement('a');
    a.href = window.URL.createObjectURL(response.body);
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(a.href);
  }

  /**
   * Platform write (spec 5.9). Raw relative URL on purpose: APIService prepends /api, and /api is
   * CSRF-exempt, so these routes live under web with CSRF. HttpClient's default XSRF interceptor
   * sends X-XSRF-TOKEN from Laravel's cookie for a relative POST. Rejects on any HTTP error.
   */
  public intakeAction(clientId: number, action: IntakeActionName, body: object): Promise<OrganizationIntakeFields & { ok?: boolean }> {
    return this.http.post<OrganizationIntakeFields & { ok?: boolean }>(
      `/platform/actions/clients/${clientId}/intake/${action}`, body, { withCredentials: true },
    ).toPromise();
  }

  /** Quotes switch (#1697). Same route group and CSRF handling as intakeAction. */
  public quotesSwitch(clientId: number, enabled: boolean): Promise<{ ok?: boolean; quotes_enabled?: boolean }> {
    return this.http.post<{ ok?: boolean; quotes_enabled?: boolean }>(
      `/platform/actions/clients/${clientId}/quotes/switch`, { enabled }, { withCredentials: true },
    ).toPromise();
  }
}
